import React, { ReactNode, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { CircleMarker } from 'react-leaflet';
import { useSelector } from 'react-redux';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_URL,
  ESRI_SATELLITE_TILE_ATTRIBUTION,
} from 'components/InteractiveMap';
import CanvasHeatLayer from 'components/InteractiveMap/CanvasHeatLayer';
import Icon from 'components/DesignSystem/Icon';
import {
  AgroOverviewLocation,
  AgroOverviewMapVariable,
  MapValuePoint,
  RiskLevel,
} from 'types/Meteorology/Agro/Overview/Dashboard';
import {
  RISK_COLOR,
  removerSufixoUf,
} from 'helper/Meteorology/Agro/Overview/locationsTable';
import {
  colorScaleForVariable,
  derivedNeutralHeatConfig,
  isLightScaleColor,
  scaleStopLabel,
} from 'helper/Meteorology/Agro/Overview/colorScale';
import {
  MapPeriod,
  formatMapPeriod,
} from 'helper/Meteorology/Agro/Overview/mapPeriod';
import {
  selectAgroOverviewLayers,
  selectAgroOverviewSelectedVariable,
} from 'redux/reducers/meteorologyAgroOverviewDashboardReducer';
import { cssVar } from 'constants/theme';
import BrazilStatesLayer from 'components/Meteorology/Agro/BrazilStatesLayer';
import BrazilCountryLayer from '../../BrazilCountryLayer';

import './index.scss';

export default function MapCard({
  locations,
  valuePoints,
  variables,
  period,
  loading,
  error,
  onOpenVariable,
  onOpenLayers,
  onOpenFullscreen,
  onSelectLocation,
}: {
  locations: AgroOverviewLocation[];
  valuePoints: MapValuePoint[];
  variables: AgroOverviewMapVariable[];
  period: MapPeriod | null;
  loading: boolean;
  /** Aviso de falha do mapa: ocupa o lugar do mapa, e o seletor de variável continua. */
  error?: ReactNode;
  onOpenVariable: () => void;
  onOpenLayers: () => void;
  onOpenFullscreen: () => void;
  onSelectLocation: (location: AgroOverviewLocation) => void;
}) {
  const selectedVariable = useSelector(selectAgroOverviewSelectedVariable);
  const layers = useSelector(selectAgroOverviewLayers);

  const selectedVariableMeta = variables.find((v) => v.nome === selectedVariable);
  const unidade = selectedVariableMeta?.unidade ?? '';
  const variableLabel = selectedVariableMeta?.label ?? 'Variável exibida';

  // ADENDO-4: config oficial do BD tem precedência; fallback = escala neutra.
  const scale = useMemo(
    () => colorScaleForVariable(
      selectedVariable,
      valuePoints.map((p) => p.valor),
      selectedVariableMeta?.config,
    ),
    [selectedVariable, valuePoints, selectedVariableMeta],
  );

  const heatPoints = useMemo(
    () => valuePoints.map(
      (p) => [p.latitude, p.longitude, p.valor] as [number, number, number],
    ),
    [valuePoints],
  );
  const heatConfig = useMemo(
    () => selectedVariableMeta?.config
      ?? derivedNeutralHeatConfig(valuePoints.map((p) => p.valor))
      ?? undefined,
    [selectedVariableMeta, valuePoints],
  );

  const variableButton = (
    <button
      type="button"
      className="agro-overview-mobile-map-variable"
      onClick={onOpenVariable}
      aria-label="Variável exibida"
    >
      <span className="agro-overview-mobile-map-variable-label">Variável exibida</span>
      <span className="agro-overview-mobile-map-variable-value">
        {variableLabel}
        <Icon iconType="down_arrow" color={cssVar('text-strong')} size="small" />
      </span>
    </button>
  );

  // Mapa em falha (#2009, item 2): o aviso ocupa o lugar do mapa, mas o seletor segue
  // à mão para trocar de variável (a escolha fica no Redux e só um recarregamento a trocava).
  // Sem a lista de variáveis (a rota delas falhou) não há o que escolher.
  if (error) {
    return (
      <div className="agro-overview-mobile-map-card">
        {variables.length > 0 && variableButton}
        {error}
      </div>
    );
  }

  return (
    <div className="agro-overview-mobile-map-card">
      <div className="agro-overview-mobile-map-header">
        <span className="agro-overview-mobile-map-title">Distribuição no Brasil</span>
        <div className="agro-overview-mobile-map-header-actions">
          <button
            type="button"
            className="agro-overview-mobile-map-chip"
            onClick={onOpenLayers}
          >
            <Icon iconType="layers" color={cssVar('text-strong')} size="small" />
            Camadas
          </button>
          <button
            type="button"
            className="agro-overview-mobile-map-icon-btn"
            onClick={onOpenFullscreen}
            aria-label="Tela cheia"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke={cssVar('text-strong')}
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
            </svg>
          </button>
        </div>
      </div>

      {variableButton}

      <div className="agro-overview-mobile-map-scale">
        <div className="agro-overview-mobile-map-scale-label">
          {`Escala da variável${unidade ? ` (${unidade})` : ''}`}
        </div>
        <div
          className="agro-overview-mobile-map-scale-bar"
          style={{ gridTemplateColumns: `repeat(${scale.stops.length || 1}, 1fr)` }}
        >
          {scale.stops.map((f) => (
            <span
              key={`${f.min}-${f.max}`}
              className={isLightScaleColor(f.color) ? 'on-light-scale-color' : undefined}
              style={{ backgroundColor: f.color }}
            >
              {scaleStopLabel(f)}
            </span>
          ))}
        </div>
      </div>

      <div className="agro-overview-mobile-map-wrapper position-relative">
        <InteractiveMap
          tileUrl={ESRI_SATELLITE_TILE_URL}
          tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
          preferCanvas
          markers={layers.localidades ? locations.map((l) => ({
            locationId: l.id,
            position: [l.latitude, l.longitude] as [number, number],
            label: l.estado
              ? `${removerSufixoUf(l.nome, l.estado)} / ${l.estado}`
              : l.nome,
            onClick: () => onSelectLocation(l),
          })) : []}
        >
          <CanvasHeatLayer points={heatPoints} config={heatConfig} />
          {layers.alertas && locations
            .filter((l): l is AgroOverviewLocation & { risco7d: RiskLevel } => (
              l.risco7d !== null
            ))
            .map((l) => (
              <CircleMarker
                key={`alerta-${l.id}`}
                center={[l.latitude, l.longitude] as [number, number]}
                radius={10}
                pathOptions={{
                  color: '#ffffff', // tema: fixo (desenhado sobre o mapa)
                  weight: 2,
                  fillColor: RISK_COLOR[l.risco7d],
                  fillOpacity: 0.95,
                }}
              />
            ))}
          {layers.limitesEstaduais
            ? <BrazilStatesLayer />
            : <BrazilCountryLayer />}
        </InteractiveMap>

        {loading && (
          <div className="agro-overview-mobile-map-loading">
            <Spinner animation="border" />
          </div>
        )}

        <div className="agro-overview-mobile-map-period">
          {formatMapPeriod(period)}
        </div>
      </div>

      <div className="agro-overview-mobile-map-hint">
        <svg
          className="agro-overview-mobile-map-hint-icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 21s-7-5.686-7-11a7 7 0 0 1 14 0c0 5.314-7 11-7 11Z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
        <span className="agro-overview-mobile-map-hint-text">
          Toque num
          {' '}
          <strong>ponto</strong>
          {' '}
          para a localidade · toque no mapa para ampliar.
        </span>
      </div>
    </div>
  );
}
